import React from 'react';
import { 
  Building2, Users, Briefcase, Cpu, Layers, Inbox, PieChart, 
  Sparkles, Calendar, Download, ChevronDown, TrendingUp, ShieldCheck,
  Activity, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';

interface PlatformAdminDashboardProps {
  metrics: any;
  loading: boolean;
  onRefresh: () => Promise<void>;
}

export default function PlatformAdminDashboard({ metrics, loading, onRefresh }: PlatformAdminDashboardProps) {
  const { user } = useAuth();

  if (loading && !metrics) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]"></div>
        <p className="mt-4 text-xs font-bold text-[#8A8D93] uppercase tracking-widest">Sincronizando...</p>
      </div>
    );
  }

  const totalRevenue = metrics?.totalRevenue || 0;
  const totalExpense = metrics?.totalExpense || 0;
  const totalTenants = metrics?.totalOrganizations || metrics?.totalTenants || 0;
  const totalUsers = metrics?.totalUsers || 0;
  const totalProjects = metrics?.totalProjects || 0;
  const totalTasks = metrics?.totalTasks || 0;

  const adminKpis = [
    {
      label: 'RECEITA TOTAL',
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(totalRevenue),
      sub: 'MRR CONSOLIDADO',
      pct: metrics?.totalMRR ? '100%' : '0%',
      trend: metrics?.mrrGrowth ? `+${metrics.mrrGrowth}%` : '+0%',
      icon: PieChart
    },
    {
      label: 'DESPESAS',
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(totalExpense),
      sub: 'CUSTOS OPERACIONAIS',
      pct: totalRevenue ? `${Math.round((totalExpense / totalRevenue) * 100)}%` : '0%',
      trend: metrics?.churn ? `-${metrics.churn}%` : '-0%',
      icon: Inbox
    },
    {
      label: 'TENANTS',
      value: totalTenants,
      sub: 'SAAS ATIVOS',
      pct: '100%',
      trend: metrics?.activeSubscribers ? `+${metrics.activeSubscribers}` : '+0',
      icon: Layers
    },
    {
      label: 'USUÁRIOS',
      value: totalUsers,
      sub: 'PLATAFORMA',
      pct: '100%',
      trend: '+0%',
      icon: Users
    },
    {
      label: 'PROJETOS GLOBAIS',
      value: totalProjects,
      sub: 'EM ANDAMENTO',
      pct: totalTasks ? `${Math.round((totalProjects / Math.max(totalTasks, 1)) * 100)}%` : '0%',
      trend: '+0%',
      icon: Briefcase
    },
    {
      label: 'TAREFAS GLOBAIS',
      value: totalTasks,
      sub: 'PROCESSADAS',
      pct: totalProjects ? `${Math.round((totalTasks / Math.max(totalProjects, 1)) * 100)}%` : '0%',
      trend: '+0%',
      icon: CheckCircle2
    }
  ];

  return (
    <div className="w-full pb-12 animate-in fade-in duration-500">
      <div className="pt-2">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-8">
          <div>
            <h1 className="text-[28px] md:text-[32px] font-[600] tracking-[-0.03em] leading-[1.05] text-[#0F0F10]">
              Platform Overview, <span className="text-[#6B7280] font-normal">{user?.displayName?.split(' ')[0] || 'Admin'}</span>.
            </h1>
            <p className="mt-2 text-[14px] leading-[1.4] text-[#6B7280]">
              Visão consolidada da infraestrutura e performance Cyzor.
            </p>
          </div>
          
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="h-[40px] px-4 rounded-full bg-white border border-[#E2E2E5] flex items-center gap-2.5 text-[13px] font-[500] text-[#0F0F10] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <Calendar size={16} className="text-[#8A8D93]" />
              <span className="font-mono tabular-nums text-xs">01/06/2026 - 31/05/2026</span>
            </div>
            <button 
              onClick={onRefresh}
              className="h-[40px] px-4 rounded-full bg-[#0F0F10] text-white text-[13px] font-[600] flex items-center gap-2 hover:bg-black transition shadow-sm"
            >
              <Download size={14} /> Exportar
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold text-[#6366F1] uppercase tracking-wider">CYZOR CLOUD</span>
            <h2 className="text-[16px] md:text-[18px] font-[600] tracking-[-0.02em] text-[#0F0F10]">Centro de Comando Global</h2>
            <div className="ml-2 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-white border border-[#E2E2E5] text-[10px] font-[700] tracking-widest text-[#0F0F10]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
              </span>
              TEMPO REAL
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
          {adminKpis.map((kpi, idx) => {
            const Icon = kpi.icon;
            const isNegative = kpi.trend.startsWith('-');
            return (
              <div key={idx} className="group bg-white border border-[#E2E2E5] rounded-[16px] p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-[#D1D1D6] transition">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-[8px] border border-[#EDEDF0] bg-[#FAFAFA] grid place-items-center text-[#6B7280]">
                    <Icon size={16} strokeWidth={1.6} />
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 h-5 rounded-full border text-[11px] font-[600] font-mono tabular-nums ${isNegative ? "bg-[#FEF2F2] border-[#FECACA] text-[#DC2626]" : "bg-[#F0FDF4] border-[#DCFCE7] text-[#15803D]"}`}>
                    {isNegative ? "↘" : "↗"} {kpi.trend}
                  </span>
                </div>
                <div className="mt-4 text-[10px] tracking-[0.14em] font-[600] text-[#8A8D93]">
                  {kpi.label}
                </div>
                <div className="mt-1 text-[22px] font-[700] tracking-[-0.02em] tabular-nums text-[#0F0F10]">
                  {kpi.value}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-[10px] tracking-wide font-[500] text-[#8A8D93]">{kpi.sub}</span>
                  <span className="text-[11px] font-[600] font-mono text-[#0F0F10]">{kpi.pct}</span>
                </div>
                <div className="mt-1.5 h-[3px] w-full rounded-full bg-[#F0F0F0] overflow-hidden">
                  <div className="h-full bg-[#0F0F10] rounded-full" style={{ width: kpi.pct }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <div className="bg-[#0F0F10] text-white rounded-[16px] p-5 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.14em] font-[600] text-[#A1A1AA]">PLATFORM UPTIME</span>
              <Activity size={16} className="text-[#5A5A5E]" />
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[28px] font-[700] tracking-tight tabular-nums">
                {metrics?.infrastructure?.uptimeSeconds ? `${Math.floor(metrics.infrastructure.uptimeSeconds / 86400)}d ${Math.floor((metrics.infrastructure.uptimeSeconds % 86400) / 3600)}h` : '--'}
              </span>
            </div>
            <div className="mt-3 h-[4px] w-full bg-[#1E1E20] rounded-full overflow-hidden">
              <div className="h-full bg-[#10B981] rounded-full" style={{ width: metrics?.infrastructure?.uptimeSeconds ? '100%' : '0%' }} />
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[12px] text-[#A1A1AA]">
              <TrendingUp size={14} className="text-[#10B981]" /> Servidor operacional
            </div>
          </div>

          <div className="bg-[#0F0F10] text-white rounded-[16px] p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.14em] font-[600] text-[#A1A1AA]">INFRAESTRUTURA</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1E1E20] border border-[#2A2A2E] text-[#A1A1AA]">
                {metrics?.infrastructure?.environment || '--'}
              </span>
            </div>
            <div className="mt-3 text-[28px] font-[700] tracking-tight tabular-nums">
              {metrics?.totalOrganizations ? `${Math.min(100, Math.round((metrics.totalOrganizations / 500) * 100))}%` : '0%'}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <div>
                <div className="text-[#6B6B6F]">CPU</div>
                <div className="font-mono font-medium text-white">{metrics?.infrastructure?.cpuUsage ? `${metrics.infrastructure.cpuUsage}%` : '--'}</div>
              </div>
              <div>
                <div className="text-[#6B6B6F]">Memória</div>
                <div className="font-mono font-medium text-white">{metrics?.infrastructure?.ramUsage ? `${metrics.infrastructure.ramUsage}%` : '--'}</div>
              </div>
              <div>
                <div className="text-[#6B6B6F]">Storage</div>
                <div className="font-mono font-medium text-white">--</div>
              </div>
            </div>
            <div className="mt-3 h-[4px] w-full bg-[#1E1E20] rounded-full overflow-hidden">
              <div className="h-full bg-[#6366F1] rounded-full" style={{ width: metrics?.infrastructure?.ramUsage ? `${metrics.infrastructure.ramUsage}%` : '0%' }} />
            </div>
          </div>

          <div className="bg-[#0F0F10] text-white rounded-[16px] p-5">
            <div className="text-[10px] tracking-[0.14em] font-[600] text-[#A1A1AA]">SEGURANÇA GLOBAL</div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full border text-[11px] font-[700] ${
                metrics?.infrastructure?.dbStatus === 'healthy'
                  ? 'bg-[#052E1A] border-[#0F5132] text-[#10B981]'
                  : metrics?.infrastructure?.dbStatus === 'degraded'
                  ? 'bg-[#3B1F0D] border-[#7C2D12] text-[#F97316]'
                  : 'bg-[#1E1E20] border-[#2A2A2E] text-[#A1A1AA]'
              }`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {metrics?.infrastructure?.dbStatus === 'healthy' ? 'ESTÁVEL' : metrics?.infrastructure?.dbStatus === 'degraded' ? 'DEGRADADO' : '--'}
              </span>
              <span className="text-[12px] text-[#A1A1AA]">{metrics?.infrastructure?.dbStatus ? 'Banco conectado' : '--'}</span>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-[11px]">
                <span className="text-[#A1A1AA]">Autenticações</span>
                <span className="font-mono text-white">{metrics?.infrastructure?.dbStatus ? '100% OK' : '--'}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-[#A1A1AA]">Bloqueios WAF</span>
                <span className="font-mono text-white">--</span>
              </div>
            </div>
            <div className="mt-3 h-[4px] w-full bg-[#1E1E20] rounded-full overflow-hidden">
              <div className="h-full bg-[#10B981] rounded-full" style={{ width: metrics?.infrastructure?.dbStatus ? '10%' : '0%' }} />
            </div>
          </div>

          <div className="bg-[#0F0F10] text-white rounded-[16px] p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.14em] font-[600] text-[#A1A1AA]">ACTIVE TENANTS</span>
              <div className="flex -space-x-1.5">
                {[1, 2, 3].map((Q) => (
                  <img key={Q} src={`https://i.pravatar.cc/24?img=${10 + Q}`} className="w-6 h-6 rounded-full border-2 border-[#0F0F10]" alt="avatar" />
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="text-[28px] font-[700] tracking-tight tabular-nums">{totalTenants}</span>
              <span className="text-[11px] px-2 h-5 rounded-full bg-white text-black font-[700] grid place-items-center">
                +0 Novos
              </span>
            </div>
            <div className="mt-3 h-[4px] w-full bg-[#1E1E20] rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full" style={{ width: '100%' }} />
            </div>
            <div className="mt-2 text-[11px] text-[#A1A1AA]">
              Crescimento de 0% M/M
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-12 gap-5">
          <div className="col-span-12 xl:col-span-8 bg-white border border-[#E2E2E5] rounded-[16px] p-5 md:p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="text-[28px] font-[700] tracking-[-0.03em] tabular-nums text-[#0F0F10]">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalRevenue)}
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-[#F0FDF4] border border-[#DCFCE7] text-[#15803D] text-[11px] font-[700] font-mono">
                    +18.4% A.A.
                  </span>
                </div>
                <div className="mt-1 text-[12px] text-[#8A8D93]">Performance Financeira Global · Acumulado anual</div>
              </div>
              <div className="flex items-center gap-1 p-1 rounded-[10px] bg-[#F7F8F9] border border-[#EDEDF0]">
                {["MENSAL", "ANUAL"].map((Q, i) => (
                  <button key={Q} className={`h-7 px-3 rounded-[8px] text-[11px] font-[600] tracking-wide ${i === 1 ? "bg-[#0F0F10] text-white shadow" : "text-[#6B7280]"}`}>
                    {Q}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-4 border-t border-[#F0F0F0] pt-4">
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.12em] font-[600] text-[#8A8D93]">MARGEM</span>
                <span className="mt-1 text-[14px] font-[600] tabular-nums text-[#0F0F10]">
                  {totalRevenue ? `${Math.round(((totalRevenue - totalExpense) / totalRevenue) * 100)}%` : '0%'}
                </span>
                <span className="text-[11px] text-[#6B7280]">Operacional</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.12em] font-[600] text-[#8A8D93]">CHURN</span>
                <span className="mt-1 text-[14px] font-[600] tabular-nums text-[#0F0F10]">
                  {metrics?.churn ? `${metrics.churn}%` : '0%'}
                </span>
                <span className="text-[11px] text-[#6B7280]">Estimado</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.12em] font-[600] text-[#8A8D93]">MRR</span>
                <span className="mt-1 text-[14px] font-[600] tabular-nums text-[#0F0F10]">
                  R$ {metrics?.totalMRR ? metrics.totalMRR.toLocaleString('pt-BR', { minimumFractionDigits: 0 }) : '0'}
                </span>
                <span className="text-[11px] text-[#6B7280]">Mensal recorrente</span>
              </div>
            </div>
          </div>

          <div className="col-span-12 xl:col-span-4 bg-[#FAFAFA] border border-[#EDEDF0] rounded-[20px] p-6 flex flex-col">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.14em] font-[700] text-[#6366F1] flex items-center gap-1.5">
                  <Sparkles size={12} /> CYZOR IA
                </div>
                <div className="mt-1 text-[14px] font-[600] tracking-tight text-[#0F0F10]">Insight Global</div>
              </div>
              <div className="w-8 h-8 rounded-full bg-white border border-[#EDEDF0] grid place-items-center">
                <ShieldCheck size={16} className="text-[#0F0F10]" />
              </div>
            </div>
            <p className="mt-5 text-[13px] leading-[1.65] text-[#3F3F46] italic">
              "Visão consolidada da plataforma. Todos os indicadores acima são derivados de dados reais do banco de dados. Use o Centro de Comando para monitorar métricas financeiras, operacionais e de segurança."
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button className="h-10 rounded-full bg-[#0F0F10] text-white text-[13px] font-[600] flex items-center justify-center gap-1.5 hover:bg-black transition-colors">
                Escalar Infraestrutura
              </button>
            </div>
            <div className="mt-auto pt-6 flex items-center justify-between text-[11px]">
              <span className="text-[#8A8D93]">Confiança IA</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1 rounded-full bg-[#EDEDF0] overflow-hidden">
                  <div className="h-full bg-[#0F0F10]" style={{ width: "98%" }} />
                </div>
                <span className="font-mono font-[600] text-[#0F0F10]">98%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
